import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Share2, TrendingUp, Trophy } from "lucide-react";

export default function ReferralsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get("/creator/referrals")
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="referrals-panel-loading" className="border border-white/10 bg-[#0A0A0A] p-6 mb-14">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">// Loading referrals…</div>
      </div>
    );
  }

  const totals = data?.totals || { referred_sales: 0, attributed_revenue: 0 };
  const top = data?.top_referrers || [];
  const perDrop = data?.per_drop || [];
  const hasAny = totals.referred_sales > 0;

  return (
    <section data-testid="referrals-panel" className="mb-14">
      <div className="flex items-end justify-between gap-4 mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00] flex items-center gap-2">
            <Share2 className="w-3 h-3" /> // Referral attribution
          </div>
          <div className="font-display font-black text-2xl mt-1">Top sharers driving your sales</div>
        </div>
        {hasAny && perDrop.length > 0 && (
          <button
            type="button"
            data-testid="referrals-toggle-per-drop"
            onClick={() => setExpanded((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 px-3 py-2"
          >
            {expanded ? "Hide per-drop" : "Show per-drop"}
          </button>
        )}
      </div>

      {!hasAny ? (
        <div
          data-testid="referrals-empty-state"
          className="border border-dashed border-white/15 py-10 px-6 text-center"
        >
          <Share2 className="w-6 h-6 text-white/30 mx-auto mb-3" />
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">No referred sales yet</div>
          <div className="mt-2 text-sm text-white/50">
            Every share button you click adds your ref-tag automatically. When someone buys through your link, they'll show up here.
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <StatCard
              testId="referrals-total-sales"
              label="Referred sales"
              value={totals.referred_sales}
              icon={<TrendingUp className="w-4 h-4" />}
            />
            <StatCard
              testId="referrals-total-revenue"
              label="Attributed revenue"
              value={`$${totals.attributed_revenue.toFixed(2)}`}
              highlight
              icon={<Trophy className="w-4 h-4" />}
            />
          </div>

          <div className="border border-white/10 bg-[#0A0A0A] divide-y divide-white/10">
            <div className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              // Top 5 referrers · all drops
            </div>
            {top.map((r, i) => (
              <div
                key={r.user_id}
                data-testid={`referrer-row-${r.user_id}`}
                className="p-4 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="font-mono text-[10px] w-6 h-6 flex items-center justify-center bg-[#CCFF00]/10 text-[#CCFF00] shrink-0">
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{r.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 truncate">
                      {r.user_id}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-[#CCFF00]">${r.revenue.toFixed(2)}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                    {r.sales} sale{r.sales === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {expanded && perDrop.length > 0 && (
            <div data-testid="referrals-per-drop" className="mt-6 space-y-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                // Top referrers · per drop
              </div>
              {perDrop.map((drop) => (
                <div key={drop.product_id} className="border border-white/10 bg-[#0A0A0A]">
                  <div className="px-4 py-2.5 border-b border-white/10">
                    <div className="font-display font-bold truncate">{drop.product_title}</div>
                  </div>
                  <div className="divide-y divide-white/10">
                    {drop.referrers.map((r, i) => (
                      <div
                        key={`${drop.product_id}-${r.user_id}`}
                        data-testid={`drop-referrer-${drop.product_id}-${r.user_id}`}
                        className="p-3 flex items-center justify-between gap-3 text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="font-mono text-[9px] w-5 h-5 flex items-center justify-center bg-white/10 text-white/70 shrink-0">
                            {i + 1}
                          </div>
                          <div className="font-mono truncate">{r.name}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="font-mono text-[#CCFF00]">${r.revenue.toFixed(2)}</span>
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 ml-2">
                            {r.sales} sale{r.sales === 1 ? "" : "s"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function StatCard({ testId, label, value, highlight, icon }) {
  return (
    <div
      data-testid={testId}
      className={`border p-4 ${highlight ? "border-[#CCFF00]/60 bg-[#CCFF00]/5" : "border-white/10 bg-[#0A0A0A]"}`}
    >
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
        {icon}
        {label}
      </div>
      <div className={`font-mono font-bold text-xl mt-1.5 ${highlight ? "text-[#CCFF00]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
